import styled from 'styled-components';
import {
  learnLevelName,
  learnLevels,
  learnUnlockScore,
  learnUnlockSessions,
} from '../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../common/src/model.ts';
import LockedIcon from '../../assets/learn-locked.svg?react';

// The level row every Learn exercise's start screen draws (issues #3473, #3518): the "Level" label
// and one chip per level. `unlocked` comes from the member's progress; `selectable` is what this
// build can actually play (Easy until the Medium and Hard vocabulary lands), so an unlocked level
// that is not selectable shows as coming soon rather than as locked, and a locked one carries the
// caption saying what unlocks it.

// Historically named after Rhythm Echo, where the chips started; the value is what the existing
// stories match, so it keeps the 'rhythm-' spelling for every exercise.
export const learnLevelChipTestId = 'rhythm-level-chip';

export const LevelChips = ({
  level,
  unlocked,
  selectable,
  onSelectLevel,
  testId = learnLevelChipTestId,
}: {
  level: LearnLevel;
  unlocked: LearnLevel[];
  selectable: LearnLevel[];
  onSelectLevel: (level: LearnLevel) => void;
  testId?: string;
}) => (
  <>
    <LevelLabel>Level</LevelLabel>
    <Chips>
      {learnLevels.map((candidate) => (
        <LevelChip
          key={candidate}
          level={candidate}
          selected={candidate === level}
          unlocked={unlocked.includes(candidate)}
          selectable={selectable.includes(candidate)}
          testId={testId}
          onSelect={onSelectLevel}
        />
      ))}
    </Chips>
  </>
);

// "3 sessions of 8+ at Easy": what unlocks `level`, for the caption under a locked chip
export function unlockCaption(level: LearnLevel): string | undefined {
  const previous = learnLevels[learnLevels.indexOf(level) - 1];
  if (!previous) {
    return;
  }
  return `${learnUnlockSessions} sessions of ${learnUnlockScore}+ at ${learnLevelName(previous)}`;
}

const LevelChip = ({
  level,
  selected,
  unlocked,
  selectable,
  testId,
  onSelect,
}: {
  level: LearnLevel;
  selected: boolean;
  unlocked: boolean;
  selectable: boolean;
  testId: string;
  onSelect: (level: LearnLevel) => void;
}) => {
  const enabled = unlocked && selectable;
  const caption = !unlocked ? unlockCaption(level) : !selectable ? 'Coming soon' : undefined;
  return (
    <ChipColumn>
      <Chip
        type="button"
        data-testid={testId}
        data-level={level}
        data-selected={selected ? 'true' : 'false'}
        data-locked={unlocked ? 'false' : 'true'}
        $selected={selected}
        $enabled={enabled}
        disabled={!enabled}
        aria-pressed={selected}
        onClick={() => onSelect(level)}
      >
        {!unlocked && <LockedIcon aria-hidden="true" />}
        {learnLevelName(level)}
      </Chip>
      {caption && <ChipCaption>{caption}</ChipCaption>}
    </ChipColumn>
  );
};

const LevelLabel = styled.div`
  margin-top: 28px;
  font-size: 13px;
  line-height: 18px;
  color: var(--color-text-darker);
`;

const Chips = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`;

const ChipColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Chip = styled.button<{ $selected: boolean; $enabled: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 22px;
  border: 2px solid ${({ $selected }) => ($selected ? 'var(--color-tab-selected)' : 'var(--color-divider)')};
  background-color: ${({ $selected }) => ($selected ? 'var(--color-tab-selected)' : 'transparent')};
  color: ${({ $selected }) => ($selected ? 'var(--color-background)' : 'var(--color-text)')};
  opacity: ${({ $enabled, $selected }) => ($enabled || $selected ? 1 : 0.5)};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 16px;
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'default')};
  -webkit-tap-highlight-color: transparent;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const ChipCaption = styled.div`
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: var(--color-text-darker);
`;
